<template>
  <div class="panel-wrapper">
    <p class="panel-title">{{ $t('app.campaign.newPage.recap.evaluationSection.title') }}</p>
    <div @keydown.enter="save" tabindex="0" class="evaluation-panel-content">
      <div>
        <p>{{ $t('app.campaign.newPage.recap.evaluationSection.adventureDurationText') }}&nbsp;<span class="bold">{{ skillsStore.getAdventureDuration(state?.campaign?.assessment?.modules || []) }} minutes</span></p>
        <p>{{ $t('app.campaign.newPage.recap.evaluationSection.count') }}&nbsp;<span class="bold">{{ passedModuleCount }}/{{ totalModuleCount }}</span></p>
      </div>
      <NewPonderation v-if="state.campaign" class="ponderation" v-model="state.campaign" :disableIgnored="props.disableIgnored"></NewPonderation>
      <div v-if="props.footerActivated" class="footer-text">
        <p v-safe-html="$t('app.campaign.newPage.slidingPanel.evaluation.footer1')"></p>
        <p v-if="false">{{ $t('app.campaign.newPage.slidingPanel.evaluation.footer2') }}</p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.panel-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 1.5rem;
}

.panel-title {
  color: var(--secondary-color);
  font-family: $bioSans;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}

.edit-buttons {
  width: 100%;
  display: flex;
  gap: 12px;
  justify-content: end;
}

.evaluation-panel-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 65vw;
  height: 100%;

  .ponderation {
    height: 50%;
  }

  .footer-text {
    color: var(--secondary-color);
    font-family: $bioSans;
    font-size: 12px;
    font-style: italic;
    font-weight: 400;
    line-height: normal;
  }
}
</style>

<script setup lang="ts">
import { Campaign, CampaignCreation } from '@/types/campaign';
import { moduleLabels } from '@/types/campaign';
import { useEditCampaign } from '@/queries/campaigns.query';
import NewPonderation from '@/views/app/campaigns/newCampaign/partials/NewPonderation.vue';

const appStore = useAppStore();
const skillsStore = useSkillsStore();
const { mutateAsync: editCampaign } = useEditCampaign();

const totalModuleCount = computed(() => {
  return moduleLabels.filter((label) => label !== 'social_desirability' && label !== 'self_efficacy').length;
});

const passedModuleCount = computed(() => {
  const res = state?.campaign?.assessment?.modules?.filter((m) => m !== 'social_desirability' && m !== 'self_efficacy')?.length;

  if (res === undefined || res === null) {
    return totalModuleCount.value;
  }
  return res;
});

const props = withDefaults(defineProps<{
  campaign: CampaignCreation | Campaign,
  disableIgnored?: boolean,
  footerActivated?: boolean,
}>(), {
  campaign: undefined,
  disableIgnored: false,
  footerActivated: true,
});

const state = reactive<{
  campaign: CampaignCreation
}>({
  campaign: undefined
});

const emit = defineEmits<{
  (e: 'valid', value: boolean): void,
  (e: 'save', value: CampaignCreation): void,
  (e: 'close'): void
}>();

function save() {
  emit('save', structuredClone(toRaw(state.campaign)));
  emit('close');
}

watch(props.campaign, (newCampaign, oldCampaign) => {
  if (newCampaign) {
    state.campaign = structuredClone(toRaw(newCampaign));
  }
})

onMounted(() => {
  if (props.campaign) {
    state.campaign = structuredClone(toRaw(props.campaign));
  }
});

onBeforeUnmount(async () => {
  if (!(state.campaign as Campaign).id) {
    save();
    return;
  }
  if (JSON.stringify(state?.campaign?.assessment) === JSON.stringify(props?.campaign?.assessment)) return;

  await editCampaign({
    context: {
      campaignId: state.campaign.id,
      organisationId: appStore.currentOrganisationId,
      spaceId: state.campaign.campaign.parent_workspace_id,
    },
    campaign: {
      schema_version: state.campaign.schema_version,
      id: state.campaign.id,
      campaign_version: state.campaign.campaign_version,
      branding: state.campaign.branding,
      assessment: state.campaign.assessment,
      campaign: state.campaign.campaign,
    },
    auto_activate: state.campaign.auto_activate,
  });
})

</script>